'use client';

import { useTranslation } from 'next-i18next';
import { LinkStyled } from '@components/common';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { PencilSquareIcon, VideoCameraIcon, PhoneIcon } from '@heroicons/react/20/solid';

interface InterviewSelectorContentProps {
  className?: string;
}

/**
 * @description Shared content for selecting interview type (Video / Audio / Text).
 * - Used inside the InterviewSelector modal and inline on the landing page.
 */
export const InterviewSelectorContent = ({ className }: InterviewSelectorContentProps) => {
  const { t } = useTranslation('common');
  const { isAuth, redirectToLogin } = useAuthClient();

  return (
    <div className={className}>
      <h3 className="mb-4 text-lg font-bold text-ink">
        {t('ai.interview.selector.title')}
      </h3>
      <p className="mb-6 text-sm text-ink-medium">
        {t('ai.interview.selector.description')}
      </p>

      {/* Action buttons */}
      <div className="space-y-3">
        {/* Video Interview Button */}
        <LinkStyled
          href="/ai-interview?autostart=1"
          styling="solid"
          rounding="full"
          color="secondary"
          className="flex items-center justify-center w-full gap-3"
          onClick={(e) => {
            if (!isAuth) {
              e.preventDefault();
              redirectToLogin('/ai-interview?autostart=1');
            }
          }}
        >
          <VideoCameraIcon className="size-5" />
          <span className="font-medium">{t('ai.video.interview')}</span>
        </LinkStyled>

        {/* Audio Interview Button */}
        <LinkStyled
          href="/audio-interview"
          styling="solid"
          rounding="full"
          color="primary"
          className="flex items-center justify-center w-full gap-3"
          onClick={(e) => {
            if (!isAuth) {
              e.preventDefault();
              redirectToLogin('/audio-interview');
            }
          }}
        >
          <PhoneIcon className="size-5" />
          <span className="font-medium">{t('ai.audio.interview')}</span>
        </LinkStyled>

        {/* Text Interview Button */}
        <LinkStyled
          href="/bestie?iw=1"
          styling="solid"
          rounding="full"
          color="ink"
          className="flex items-center justify-center w-full gap-3"
          onClick={(e) => {
            if (!isAuth) {
              e.preventDefault();
              redirectToLogin('/profile');
            }
          }}
        >
          <PencilSquareIcon className="size-5" />
          <span className="font-medium">{t('global.label.cv.interview.text')}</span>
        </LinkStyled>
      </div>
    </div>
  );
};

export default InterviewSelectorContent;
